import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/primitives";
import { CAPABILITIES, IMAGES } from "@/data/content";

const IMGS = [IMAGES.speaker, IMAGES.facade, IMAGES.conferenceCrowd, IMAGES.lines];

export default function CapabilitiesShowcase({ withHeader = true }) {
  const [active, setActive] = useState(0);

  return (
    <section className="py-24 md:py-36 border-b hairline" data-testid="capabilities-section">
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        {withHeader && (
          <div className="max-w-3xl mb-16">
            <Reveal><Overline>04 — Capabilities</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-heading font-light text-navy tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                The platforms that build authority.
              </h2>
            </Reveal>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="border-t hairline">
              {CAPABILITIES.map((cap, i) => (
                <div
                  key={cap.id}
                  onMouseEnter={() => setActive(i)}
                  className={`group border-b hairline py-8 md:py-10 cursor-default transition-colors duration-300 ${
                    active === i ? "bg-mist/60" : ""
                  } -mx-4 px-4`}
                  data-testid={`capability-${cap.id}`}
                >
                  <div className="flex items-baseline justify-between gap-6">
                    <h3 className={`font-heading font-light text-2xl md:text-4xl transition-colors duration-300 ${active === i ? "text-navy" : "text-navy/50"}`}>
                      {cap.title}
                    </h3>
                    <span className="font-heading text-xs text-gold tracking-widest pt-2">0{i + 1}</span>
                  </div>
                  <AnimatePresence initial={false}>
                    {active === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="mt-5 max-w-xl text-ink/70 leading-relaxed font-body md:text-lg">{cap.outcome}</p>
                        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                          {cap.points.map((p) => (
                            <span key={p} className="text-xs uppercase tracking-[0.15em] text-navy/60">{p}</span>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-32">
            <div className="relative overflow-hidden aspect-[4/5] bg-mist">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={IMGS[active]}
                  alt={CAPABILITIES[active].title}
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
